import { createReducer, createAction, PayloadAction } from "@reduxjs/toolkit";
import {
    MixtureAlgorithm,
    SdsProfileId,
    type Chemical,
} from "../../../data/models/schemas";
import { DEFAULT_SDS_COMPLIANCE_FORMAT } from "../../../components/sds/sdsComplianceOptions";
import type { SDSFormat } from "../../../types/sds-types";

export type ISdsPersistentReduxState = {
    locale_id?: string;
    profile_id: SdsProfileId;
    /** Retained for persisted-state and saved-document compatibility. */
    compliance_format?: SDSFormat;
    search_mode: "chemical" | "mixture";
    product_name?: string;
    product_cas_number?: string;
    mixture_algorithm?: MixtureAlgorithm;
    chemicals: Chemical[];
};

export const getDefaultState = (): ISdsPersistentReduxState => {
    return {
        mixture_algorithm: MixtureAlgorithm.BridgingPrinciples,
        search_mode: "chemical",
        product_name: "",
        product_cas_number: "",
        locale_id: "us-en",
        profile_id: SdsProfileId.US_OSHA_HAZCOM_EN_US,
        compliance_format: DEFAULT_SDS_COMPLIANCE_FORMAT,
        chemicals: [],
    };
};

export const setMixtureAlgorithm = createAction<MixtureAlgorithm>(
    "persistent/sds/setMixtureAlgorithm"
);

export const setSearchMode = createAction<"chemical" | "mixture">(
    "persistent/sds/setSearchMode"
);

export const setProductName = createAction<string>(
    "persistent/sds/setProductName"
);

export const setProductCas = createAction<string>(
    "persistent/sds/setProductCas"
);

export const setLocaleId = createAction<string>("persistent/sds/setLocaleId");

export const setComplianceProfile = createAction<SdsProfileId>(
    "persistent/sds/setComplianceProfile"
);

export const setComplianceFormat = createAction<SDSFormat>(
    "persistent/sds/setComplianceFormat"
);

export const setChemicals = createAction<Chemical[]>(
    "persistent/sds/setChemicals"
);

export const updateChemical = createAction<{
    index: number;
    changes: Partial<Chemical>;
}>("persistent/sds/updateChemical");

export const addChemical = createAction("persistent/sds/addChemical");

export const removeChemical = createAction<number>(
    "persistent/sds/removeChemical"
);

export const resetSdsSearch = createAction("persistent/sds/resetSdsSearch");

export const sdsPersistent = createReducer(getDefaultState(), (builder) => {
    builder.addCase(setMixtureAlgorithm, (state, action) => {
        state.mixture_algorithm = action.payload;
    });
    builder.addCase(setSearchMode, (state, action) => {
        state.search_mode = action.payload;
        if (action.payload === "chemical") {
            if (state.chemicals[0]) {
                state.chemicals = [
                    {
                        ...state.chemicals[0],
                        percent_composition: 100,
                    },
                ];
            } else {
                state.chemicals = [];
            }
        }
    });
    builder.addCase(setProductName, (state, action) => {
        state.product_name = action.payload;
    });
    builder.addCase(setProductCas, (state, action) => {
        state.product_cas_number = action.payload;
    });
    builder.addCase(setLocaleId, (state, action) => {
        state.locale_id = action.payload;
    });
    builder.addCase(setComplianceProfile, (state, action) => {
        state.profile_id = action.payload;
    });
    builder.addCase(setComplianceFormat, (state, action) => {
        state.compliance_format = action.payload;
    });
    builder.addCase(
        setChemicals,
        (state, action: PayloadAction<Chemical[]>) => {
            state.chemicals = action.payload;
        }
    );
    builder.addCase(updateChemical, (state, action) => {
        const { index, changes } = action.payload;
        const list = [...(state.chemicals || [])];
        list[index] = { ...list[index], ...changes };
        state.chemicals = list;
    });
    builder.addCase(addChemical, (state) => {
        state.chemicals = [
            ...(state.chemicals || []),
            {
                percent_composition: undefined,
            },
        ];
    });
    builder.addCase(removeChemical, (state, action) => {
        const idx = action.payload;
        state.chemicals = (state.chemicals || []).filter((_, i) => i !== idx);
    });
    builder.addCase(resetSdsSearch, () => getDefaultState());
});
